/**
 * User-facing mapping for a FAILED add-camera direct-link TRANSPORT leg (ENG-4344).
 *
 * When the camera cannot be reached at all (powered off, or on a different
 * network/subnet than this computer), the Electron main process's fetch to
 * `http://<host>:9321/api/v1/link` rejects with undici's `TypeError: fetch
 * failed`, and the renderer receives the IPC-wrapped string
 * "Error invoking remote method 'proxy-direct-link-status-request': TypeError:
 * fetch failed" (studio/electron/src/main/mdns/mdns-ipc.ts). The Authenticate
 * Source dialog used to render that raw string, so users retried credentials
 * against a camera that was never reachable in the first place.
 *
 * The plain-language explanation of this failure already existed — written for
 * HIL e2e triage as the `proxy-direct-link-fetch-failed` signature in
 * studio/electron/e2e/helpers/app-error-watch.ts ("transport failure, not an
 * HTTP error") — but never reached production users. This module lifts that
 * explanation into user-facing copy. The transport shape and an HTTP-status
 * rejection ("HTTP error: 401") map to different copy, so a camera that answered
 * is never described as unreachable and raw IPC text never reaches the user.
 *
 * This lives in its own module (sibling of direct-link-log.ts, following its
 * convention) so the matcher and copy are importable by a lightweight unit test
 * and by the dialog without pulling the heavy bridge-hooks dependency graph.
 * Per rule: think-in-systems (ai.knowledge/global).
 * Per rule: investigate-before-adding (ai.knowledge/global) — sibling
 * direct-link-log.ts is the established shape for these cross-cut contracts.
 */

/**
 * The transport-failure shapes the direct-link IPC rejection can carry:
 * - `fetch failed` — undici's TypeError when the connection never completed
 *   (the exact string the app-error-watch.ts signature matches on);
 * - the errno codes undici surfaces when the cause is included in the message.
 *
 * Deliberately anchored to these tokens and nothing broader: `HTTP error: <status>`
 * rejections from the same handler are REAL HTTP answers from a reachable camera
 * (e.g. 401 bad credentials) and must never be rewritten as "unreachable".
 */
const TRANSPORT_FAILURE_PATTERN =
  /\bfetch failed\b|ECONNREFUSED|ECONNRESET|EHOSTUNREACH|ENETUNREACH|ETIMEDOUT|EAI_AGAIN/;

/**
 * Plain-language copy shown in the Authenticate Source dialog for a transport
 * failure. Meaning lifted from app-error-watch.ts `proxy-direct-link-fetch-failed`
 * (the link POST never completed — transport failure, not an HTTP error): the
 * camera may be off, or on a different network than this computer.
 */
export const DIRECT_LINK_UNREACHABLE_MESSAGE =
  "This computer could not complete the connection to the camera. Check the camera's power and network connection, and make sure this computer can reach it. Then try again.";

/**
 * `error_type` token stamped on the analytics capture for this failure.
 * Reuses the existing `unreachable` taxonomy token
 * (analytics/constants/onboarding-events.ts ONBOARDING_ERROR_CATEGORY.UNREACHABLE)
 * rather than minting a new spelling for the same meaning.
 */
export const DIRECT_LINK_TRANSPORT_ERROR_TYPE = 'unreachable';

/**
 * Whether a direct-link failure message is the transport shape (camera
 * unreachable) rather than an HTTP/credential answer from a reachable camera.
 *
 * @param message - The rejection message from the direct-link request.
 */
export const isDirectLinkTransportFailure = (message: string): boolean =>
  TRANSPORT_FAILURE_PATTERN.test(message);

/** The status mdns-ipc.ts puts in its rejection when the camera answered non-2xx. */
const HTTP_STATUS_PATTERN = /\bHTTP error: (\d{3})\b/;

/** The prefix Electron adds to every rejection that crosses the IPC boundary. */
const IPC_REJECTION_PATTERN = /\bError invoking remote method\b/;

/** Shown when the camera answered the link request with 401/403. */
export const DIRECT_LINK_CREDENTIALS_MESSAGE =
  'The camera rejected the username or password. Check the credentials and try again.';

/** Shown when a failure has no more specific cause the user can act on. */
export const DIRECT_LINK_FAILED_MESSAGE = 'Could not link the camera. Try again.';

/**
 * Shown when the camera answered but could not complete the link request.
 *
 * @param status - HTTP status the camera answered with, when known.
 */
export const directLinkCameraErrorMessage = (status?: number): string =>
  `The camera could not complete the link request${status ? ` (HTTP ${status})` : ''}. ` +
  'Try again, and restart the camera if it keeps failing.';

/** Cause of a rejected direct-link details request, as the user can act on it. */
export type DirectLinkRequestFailure =
  | { readonly kind: 'unreachable' }
  | { readonly kind: 'credentials'; readonly status: number }
  | { readonly kind: 'camera'; readonly status: number }
  | { readonly kind: 'other' };

/**
 * Classify the rejection of a direct-link details request (the IPC proxy to the
 * camera's `/api/v1/link`): never answered, answered 401/403, answered with
 * another HTTP error, or anything else.
 *
 * @param message - The rejection message from the direct-link request.
 */
export const classifyDirectLinkRequestFailure = (message: string): DirectLinkRequestFailure => {
  if (isDirectLinkTransportFailure(message)) {
    return { kind: 'unreachable' };
  }

  const status = Number(HTTP_STATUS_PATTERN.exec(message)?.[1]);
  if (status === 401 || status === 403) {
    return { kind: 'credentials', status };
  }

  return status ? { kind: 'camera', status } : { kind: 'other' };
};

/**
 * User-facing copy for a rejected direct-link details request. Shared by the
 * single-add dialog and the Authentication tab so one camera answer reads the
 * same in both. Raw IPC text is never returned; any other message is already
 * app-authored and passes through.
 *
 * @param message - The rejection message from the direct-link request.
 */
export const describeDirectLinkRequestFailure = (message: string): string => {
  const failure = classifyDirectLinkRequestFailure(message);
  switch (failure.kind) {
    case 'unreachable':
      return DIRECT_LINK_UNREACHABLE_MESSAGE;
    case 'credentials':
      return DIRECT_LINK_CREDENTIALS_MESSAGE;
    case 'camera':
      return directLinkCameraErrorMessage(failure.status);
    default:
      return IPC_REJECTION_PATTERN.test(message) ? DIRECT_LINK_FAILED_MESSAGE : message;
  }
};
